import { useState, useMemo } from 'react'

import { Setting } from 'src/Redux/api/settingsApi'

import { ModelCalculated, OfferModelUnit } from './useCalculator'
import { useCalculator } from './useCalculator'
import {
  getDefaultToolId,
  getSettingValue,
  getUnitQuantity,
  getUnitsQuantity,
  resolveRentPrice,
} from './helpers'

/**
 * Хук для управления элементами оборудования в калькуляторе
 * Обеспечивает добавление, удаление, обновление оборудования и расчет стоимости аренды
 */

interface UseEquipmentItemsProps {
  fuelPrice: number
  partDeliveryPrice: number
  personDeliveryPrice: number
  accommodationPrice: number
  foodPrice: number
  personnelHouseholdServicesCost?: number
  dailyMedCheckCost?: number
  season: string
  settingsList?: Setting[]
}

export const useEquipmentItems = ({
  fuelPrice,
  partDeliveryPrice,
  personDeliveryPrice,
  accommodationPrice,
  foodPrice,
  personnelHouseholdServicesCost,
  dailyMedCheckCost,
  season,
  settingsList,
}: UseEquipmentItemsProps): {
  items: ModelCalculated[]
  setItems: (items: ModelCalculated[]) => void
  calculatedEquipments: ModelCalculated[]
  addItem: (equipment: ModelCalculated) => void
  removeItem: (itemId: number) => void
  updateItemProperty: <T>(
    itemId: number,
    property: keyof ModelCalculated,
    value: T,
  ) => void
  addUnit: (modelId: number) => void
  removeUnit: (modelId: number, unitIndex: number) => void
  updateUnitProperty: <T extends keyof OfferModelUnit>(
    modelId: number,
    unitIndex: number,
    property: T,
    value: OfferModelUnit[T],
  ) => void
} => {
  const [items, setItems] = useState<ModelCalculated[]>([])
  const { getTotalRent } = useCalculator()

  const medicalExaminationCost = getSettingValue(
    settingsList || [],
    'medicalExaminationCost',
  )

  // Расчет оборудования с обновленными ценами (цена считается на каждый экземпляр)
  const calculatedEquipments = useMemo(() => {
    return items.map((item) => {
      const units = item.units || []

      // Считаем цену для каждого экземпляра со своими сменами и навесным
      const calculatedUnits = units.map((unit) => {
        const unitTotalRentPricePerHour = getTotalRent({
          model: item,
          fuelPrice: fuelPrice,
          deliveryPrice: partDeliveryPrice,
          personDeliveryPrice: personDeliveryPrice,
          accommodationPrice: accommodationPrice || 0,
          foodPrice: foodPrice || 0,
          personnelHouseholdServicesCost:
            Number(personnelHouseholdServicesCost) || 0,
          relocationCost: unit.relocationCost,
          shiftsCount: unit.shiftCount,
          selectedToolPrice: item?.modelTools?.find(
            (modelTool) => modelTool?.id === unit.toolId,
          )?.toolWorkPrice,
          medicalExaminationCost: Number(medicalExaminationCost) || 0,
          dailyMedCheckCost: dailyMedCheckCost,
          season: season,
        })

        return {
          ...unit,
          totalRentPricePerHour: unitTotalRentPricePerHour,
        }
      })

      // Ставка модели — среднее по экземплярам с учётом их количества (цена
      // за 1 ед.); количество техники = сумма количеств экземпляров. КП и
      // договор сами домножают эту ставку на количество/часы, поэтому здесь
      // нельзя суммировать по экземплярам.
      const count = getUnitsQuantity(calculatedUnits)
      const totalRentPricePerHour = count
        ? calculatedUnits.reduce(
            (sum, unit) =>
              sum + (unit.totalRentPricePerHour || 0) * getUnitQuantity(unit),
            0,
          ) / count
        : 0

      return {
        ...item,
        units: calculatedUnits,
        count,
        totalRentPricePerHour,
      }
    })
  }, [
    items,
    getTotalRent,
    fuelPrice,
    partDeliveryPrice,
    personDeliveryPrice,
    accommodationPrice,
    foodPrice,
    personnelHouseholdServicesCost,
    medicalExaminationCost,
    dailyMedCheckCost,
    season,
  ])

  // Добавление элемента оборудования
  const addItem = (equipment: ModelCalculated): void => {
    setItems((prevItems) => [
      ...prevItems,
      {
        ...equipment,
        specialPrice: resolveRentPrice(
          equipment.specialPrice,
          equipment.baseRentalCost,
        ),
        count: 1,
        units: [
          {
            departurePoint: '',
            workPoint: '',
            returnPoint: '',
            deliveryCost: undefined,
            returnCost: undefined,
            quantity: 1,
            shiftCount: undefined,
            toolId: getDefaultToolId(equipment.modelTools),
          },
        ],
      },
    ])
  }

  // Удаление элемента оборудования
  const removeItem = (itemId: number): void => {
    setItems((prevItems) =>
      prevItems.filter((selectedItem) => selectedItem.id !== itemId),
    )
  }

  // Обновление свойства элемента
  const updateItemProperty = <T,>(
    itemId: number,
    property: keyof ModelCalculated,
    value: T,
  ): void => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, [property]: value } : item,
      ),
    )
  }

  // Добавление нового unit к модели
  const addUnit = (modelId: number): void => {
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === modelId) {
          const newUnit: OfferModelUnit = {
            departurePoint: '',
            workPoint: '',
            returnPoint: '',
            deliveryCost: undefined,
            returnCost: undefined,
            quantity: 1,
            shiftCount: undefined,
            toolId: getDefaultToolId(item.modelTools),
          }
          return {
            ...item,
            units: [...(item.units || []), newUnit],
          }
        }
        return item
      }),
    )
  }

  // Удаление unit из модели (минимум 1 должен остаться)
  const removeUnit = (modelId: number, unitIndex: number): void => {
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === modelId && item.units && item.units.length > 1) {
          return {
            ...item,
            units: item.units.filter((_, index) => index !== unitIndex),
          }
        }
        return item
      }),
    )
  }

  // Обновление свойства unit
  const updateUnitProperty = <T extends keyof OfferModelUnit>(
    modelId: number,
    unitIndex: number,
    property: T,
    value: OfferModelUnit[T],
  ): void => {
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === modelId && item.units) {
          const updatedUnits = item.units.map((unit, index) =>
            index === unitIndex ? { ...unit, [property]: value } : unit,
          )
          return { ...item, units: updatedUnits }
        }
        return item
      }),
    )
  }

  return {
    items,
    setItems,
    calculatedEquipments,
    addItem,
    removeItem,
    updateItemProperty,
    addUnit,
    removeUnit,
    updateUnitProperty,
  }
}
